import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { redact, redactCookies, redactQueryString } from '@multimod/shared';
import type { MeDTO } from './api.js';

// Nota: ErrorEvent/TransactionEvent são exportados apenas de @sentry/core (não instalado
// diretamente). Usamos Sentry.Event — supertipo que cobre ambos — acessível via
// @sentry/react → @sentry/browser → @sentry/core chain de re-exports.
type SentryEvent = Sentry.Event;

// TODO(sentry-breadcrumb-url): breadcrumbs do auto-fetch instrumentation podem
// carregar URLs com query strings (ex: /api?token=...). Hoje a redação só atinge
// chaves sensíveis em b.data — não parseia a string da URL. Aceitável porque
// nossos OAuth flows usam POST/redirect (token nunca aparece em URL de fetch),
// mas adicionar parser de query string em b.data.url quando código de chamadores
// externos mudar o padrão.
export function sanitizeSentryEvent<T extends SentryEvent>(event: T): T {
  const ev: Record<string, unknown> = { ...(event as Record<string, unknown>) };

  if (ev.request && typeof ev.request === 'object') {
    const req = { ...(ev.request as Record<string, unknown>) };
    if (req.headers && typeof req.headers === 'object') req.headers = redact(req.headers);
    if (req.cookies && typeof req.cookies === 'object') req.cookies = redactCookies(req.cookies);
    if (typeof req.query_string === 'string') {
      req.query_string = redactQueryString(req.query_string);
    }
    if (req.data !== undefined) {
      // Paridade com worker: bodies de fetch para /auth/* ou /me/* substituídos
      // por sentinela em vez de redaction key-by-key (campos OAuth variam por provider).
      const url = typeof req.url === 'string' ? req.url : '';
      if (/\/(auth|me)\b/.test(url)) {
        req.data = '[REDACTED:auth-body]';
      } else {
        req.data = redact(req.data);
      }
    }
    ev.request = req;
  }

  if (Array.isArray(ev.breadcrumbs)) {
    ev.breadcrumbs = (ev.breadcrumbs as Array<Record<string, unknown>>).map((b) => ({
      ...b,
      data: b.data !== undefined ? redact(b.data) : b.data,
    }));
  }

  if (ev.user && typeof ev.user === 'object') {
    const u = ev.user as Record<string, unknown>;
    ev.user = {
      ...(typeof u.id === 'string' || typeof u.id === 'number' ? { id: u.id } : {}),
      ...(typeof u.username === 'string' ? { username: u.username } : {}),
    };
  }

  if (ev.extra) ev.extra = redact(ev.extra) as Record<string, unknown>;
  if (ev.tags) ev.tags = redact(ev.tags) as Record<string, string>;
  if (ev.contexts) ev.contexts = redact(ev.contexts) as Record<string, Record<string, unknown>>;

  return ev as unknown as T;
}

/**
 * Sincroniza o contexto de usuário Sentry com o estado da sessão.
 * Deve ser chamado no componente raiz (App) após QueryClientProvider estar montado.
 * Limpa o contexto (setUser(null)) quando a sessão é destruída (logout/revoke).
 *
 * Spec D3: { id, username: "twitch:<displayName>, youtube:<displayName>" }
 */
export function useSentryUser(data: MeDTO | null | undefined): void {
  const id = data?.user.id;
  const twitchHandle = data?.connections.twitch?.displayName;
  const youtubeHandle = data?.connections.youtube?.displayName;
  useEffect(() => {
    if (!id) {
      Sentry.setUser(null);
      return;
    }
    const handles: string[] = [];
    if (twitchHandle) handles.push(`twitch:${twitchHandle}`);
    if (youtubeHandle) handles.push(`youtube:${youtubeHandle}`);
    Sentry.setUser({
      id,
      ...(handles.length ? { username: handles.join(', ') } : {}),
    });
    // Deps finos: TanStack Query retorna referência nova de `data` em refetches
    // (focus/intervalo). Observar só os campos que de fato compõem o setUser
    // evita chamada redundante a cada refetch.
  }, [id, twitchHandle, youtubeHandle]);
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // no-op em dev

  const gitSha = import.meta.env.VITE_GIT_SHA;
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV,
    ...(gitSha ? { release: gitSha } : {}),
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 1.0,
    sendDefaultPii: false,
    // enableLogs substitui _experiments.enableLogs (deprecated em v9)
    enableLogs: true,
    beforeSend: sanitizeSentryEvent,
    beforeSendTransaction: sanitizeSentryEvent,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'system',
      }),
    ],
  });
}
